import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import cartService, { Cart, CartTotals, CartResponse, CartItem } from '../../services/cartService';
import { tokenService } from '../../services/tokenService';

interface CartContextType {
    cart: Cart | null;
    totals: CartTotals | null;
    loading: boolean;
    error: string | null;
    itemCount: number;
    refreshCart: () => Promise<void>;
    addItem: (productId: number, priceId: number, quantity?: number) => Promise<CartItem | null>;
    updateItemQuantity: (itemId: number, quantity: number) => Promise<void>;
    removeItem: (itemId: number) => Promise<void>;
    clearCart: () => Promise<void>;
    isProductInCart: (productId: number) => boolean;
    isCartFree: () => boolean;
    hasSubscriptions: () => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

interface CartProviderProps {
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const [cart, setCart] = useState<Cart | null>(null);
    const [totals, setTotals] = useState<CartTotals | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const updateCartState = useCallback((response: CartResponse) => {
        setCart(response.cart);
        setTotals(response.totals);
        setError(null);
    }, []);

    const refreshCart = useCallback(async () => {
        // Ohne gültigen Token würde /api/cart mit 401 antworten — kein Sinn,
        // es überhaupt zu versuchen, und kein Grund, die Console mit
        // "Error loading cart" zu spammen während der Login-Flow läuft.
        if (!tokenService.hasValidToken()) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const response = await cartService.getCart();
            updateCartState(response);
        } catch (err) {
            console.error('Error loading cart:', err);
            setError('Warenkorb konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    }, [updateCartState]);

    const addItem = useCallback(async (productId: number, priceId: number, quantity: number = 1): Promise<CartItem | null> => {
        try {
            const response = await cartService.addItem({ productId, priceId, quantity });
            updateCartState(response);
            return response.item;
        } catch (err) {
            console.error('Error adding item:', err);
            setError('Produkt konnte nicht hinzugefuegt werden');
            return null;
        }
    }, [updateCartState]);

    const updateItemQuantity = useCallback(async (itemId: number, quantity: number) => {
        try {
            const response = await cartService.updateItemQuantity(itemId, quantity);
            updateCartState(response);
        } catch (err) {
            console.error('Error updating item:', err);
            setError('Menge konnte nicht aktualisiert werden');
        }
    }, [updateCartState]);

    const removeItem = useCallback(async (itemId: number) => {
        try {
            const response = await cartService.removeItem(itemId);
            updateCartState(response);
        } catch (err) {
            console.error('Error removing item:', err);
            setError('Produkt konnte nicht entfernt werden');
        }
    }, [updateCartState]);

    const clearCart = useCallback(async () => {
        try {
            const response = await cartService.clearCart();
            updateCartState(response);
        } catch (err) {
            console.error('Error clearing cart:', err);
            setError('Warenkorb konnte nicht geleert werden');
        }
    }, [updateCartState]);

    const isProductInCart = useCallback((productId: number): boolean => {
        if (!cart) return false;
        return cartService.isProductInCart(cart, productId);
    }, [cart]);

    const isCartFree = useCallback((): boolean => {
        if (!cart) return false;
        return cartService.isCartFree(cart);
    }, [cart]);

    const hasSubscriptions = useCallback((): boolean => {
        if (!cart) return false;
        return cartService.hasSubscriptions(cart);
    }, [cart]);

    const itemCount = cart ? cartService.getItemCount(cart) : 0;

    useEffect(() => {
        refreshCart();
    }, [refreshCart]);

    const value: CartContextType = {
        cart,
        totals,
        loading,
        error,
        itemCount,
        refreshCart,
        addItem,
        updateItemQuantity,
        removeItem,
        clearCart,
        isProductInCart,
        isCartFree,
        hasSubscriptions,
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export default CartContext;
